import moment from "moment";
import { CleanerEventRow, CleanerRef, CleanerStoreRow } from "./cleanerAttendanceTypes";

export const DUE_AFTER_MS = 24 * 60 * 60 * 1000;
export const OVERDUE_AFTER_MS = 48 * 60 * 60 * 1000;

export type StoreAttendanceStatus = "onsite" | "ok" | "due" | "overdue" | "never";

export function storeAttendanceStatus(
  row: CleanerStoreRow,
  now: number = Date.now()
): StoreAttendanceStatus {
  if (row.cleanerOnSiteNow) return "onsite";
  if (!row.lastAttended) return "never";
  const outMs = Date.parse(row.lastAttended.clockOut);
  if (!Number.isFinite(outMs)) return "never";
  const age = now - outMs;
  if (age > OVERDUE_AFTER_MS) return "overdue";
  if (age > DUE_AFTER_MS) return "due";
  return "ok";
}

export const STATUS_SORT_RANK: Record<StoreAttendanceStatus, number> = {
  overdue: 0,
  never: 1,
  due: 2,
  onsite: 3,
  ok: 4,
};

export function statusLabel(status: StoreAttendanceStatus): string {
  switch (status) {
    case "onsite":
      return "On site";
    case "ok":
      return "Attended";
    case "due":
      return "Due";
    case "overdue":
      return "Overdue";
    default:
      return "Never";
  }
}

export function statusTone(status: StoreAttendanceStatus): "good" | "info" | "warn" | "bad" | "neutral" {
  switch (status) {
    case "onsite":
      return "good";
    case "ok":
      return "info";
    case "due":
      return "warn";
    case "overdue":
      return "bad";
    default:
      return "neutral";
  }
}

export function lastAttendedMs(row: CleanerStoreRow): number | null {
  if (!row.lastAttended) return null;
  const ms = Date.parse(row.lastAttended.clockOut);
  return Number.isFinite(ms) ? ms : null;
}

/** Worst first, then the longest since last attended. */
export function compareStoresByUrgency(
  a: CleanerStoreRow,
  b: CleanerStoreRow,
  now: number = Date.now()
): number {
  const rank =
    STATUS_SORT_RANK[storeAttendanceStatus(a, now)] -
    STATUS_SORT_RANK[storeAttendanceStatus(b, now)];
  if (rank !== 0) return rank;
  const aMs = lastAttendedMs(a) ?? -Infinity;
  const bMs = lastAttendedMs(b) ?? -Infinity;
  if (aMs !== bMs) return aMs < bMs ? -1 : 1;
  return a.name.localeCompare(b.name);
}

export function cleanerName(cleaner: CleanerRef | null | undefined): string {
  if (!cleaner) return "—";
  return cleaner.name?.trim() || `Cleaner #${cleaner.id}`;
}

export function formatAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.fromNow() : "—";
}

export function formatAbsolute(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = moment(iso);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : iso;
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = moment(iso);
  if (!m.isValid()) return "—";
  return m.isSame(moment(), "day") ? m.format("HH:mm") : m.format("DD/MM HH:mm");
}

export function formatSessionDuration(
  clockIn: string | null | undefined,
  clockOut: string | null | undefined
): string | null {
  if (!clockIn || !clockOut) return null;
  const ms = Date.parse(clockOut) - Date.parse(clockIn);
  if (!Number.isFinite(ms) || ms < 0) return null;
  const totalMinutes = Math.round(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

export function eventLabel(event: CleanerEventRow["event"]): string {
  return event === "clock_in" ? "Clock in" : "Clock out";
}

export function formatDistance(metres: number | null | undefined): string {
  if (metres == null || !Number.isFinite(metres)) return "";
  if (metres >= 1000) return `${(metres / 1000).toFixed(1)} km`;
  return `${Math.round(metres)} m`;
}

export function googleMapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}
